import Event from '../events';
import EventHandler from '../event-handler';
import { logger } from '../utils/logger';
//import { ErrorTypes, ErrorDetails } from '../errors';
import BufferHelper from '../helper/buffer-helper';

class StreamCoordinator extends EventHandler {
	constructor(hls) {
		super(hls,
			Event.MEDIA_ATTACHED,
			Event.MEDIA_DETACHING,
			Event.MANIFEST_LOADING,
			Event.MANIFEST_PARSED, //main manifest parsed
			Event.AUDIO_TRACKS_UPDATED, //audio manifest parsed
			Event.SUBTITLE_TRACKS_UPDATED, //subtitle manifest parsed
			Event.LEVEL_UPDATED, //main level parsed
			Event.AUDIO_TRACK_UPDATED, //audio level parsed
			Event.SUBTITLE_TRACK_UPDATED, //subtitle level parsed
			Event.AUDIO_TRACK_SWITCHING,
			Event.STREAM_DESYNC,
			Event.FRAG_BUFFERED,
			Event.BUFFER_FLUSHING,
			Event.ERROR
		);

		/*
			There are three pairs of controllers:
			main(video/in-mux audio/in-mux subtitles/audio only streams) - level-controller and stream-controller
			(in-mux subtitles are parsed by timeline-controller but the main pair handles loading)
			audio(latebound audio) - audio-track-controller and audio-stream-controller
			subtitle(latebound subtitles) - subtitle-track-controller and subtitle-stream-controller

			In all cases all three pairs will emit their manifest parsed event. Future events depend on if latebound audio/subtitles are active
		*/

		this.config = this.hls.config;
		this.media = null;

		this._monitorMainControllers = false;
		this._monitorAudioControllers = false;
		this._monitorSubtitleControllers = false;
		this._controllersParsed = 0;
		this._activeControllers = 0;
		this._mainControllersReady = false;
		this._audioControllersReady = false;
		this._subtitleControllersReady = false;
		this._allReady = false;
		this._audioControllersActive = false; //when a stream has both in-mux and latebound audio, we want to stop listening to the audio controller on in-mux

		this._mainLiveTime = undefined;
		this._audioLiveTime = undefined;
		this._mainLevelStartTime = undefined;
		this._mainLevelEndTime = undefined;
		this._audioLevelStartTime = undefined;
		this._audioLevelEndTime = undefined;
		this._combinedLiveTime = undefined;
		this._mainBasePdt = undefined;
		this._audioBasePdt = undefined;
		this._basePdt = undefined;
		this._startPdt = undefined;
		this._resumeBasePdt = undefined;

		this._mainLive = true;
		this._audioLive = true;
		this._startTime = -1;
		this._liveStreamsStartAtLiveTime = true;

		this._mainStreamTimeoutTimer = null;
		this._audioStreamTimeoutTimer = null;
		this._mainTimedOut = false;
		this._audioTimedOut = false;
		this._audioForcedUnpause = false;
		this._vidReadyForDispose = false;
		this._audReadyForDispose = false;
		this._defaultTimeout = 30000;

		this._targetDuration = undefined;
	}

	dispose() {
		this.disarmMainStreamTimeout();
		this.disarmAudioStreamTimeout();

		this.config = null;
		this.media = null;
		this.hls = null;
	}

	onMediaAttached(data) {
		this.media = data.media;
		this.onvseeking = this.onMediaSeeking.bind(this);
		this.onvended = this.onMediaEnded.bind(this);
		this.media.addEventListener('seeking', this.onvseeking);
		this.media.addEventListener('ended', this.onvended);
		this.resetStreamTimeout();
	}

	onMediaDetaching() {
		if(this.media) {
			this.media.removeEventListener('seeking', this.onvseeking);
			this.media.removeEventListener('ended', this.onvended);
		}
	}

	resetStreamTimeout() {
		if(this._mainStreamTimeoutTimer) {
			this.disarmMainStreamTimeout();
			this.armMainStreamTimeout();
		}
		if(this._audioStreamTimeoutTimer) {
			this.disarmAudioStreamTimeout();
			this.armAudioStreamTimeout();
		}
	}

	onMediaSeeking() {
		this.resetStreamTimeout();
	}

	onBufferFlushing() {
		this.resetStreamTimeout();
	}

	onMediaEnded() {
		this.disarmMainStreamTimeout();
		this.disarmAudioStreamTimeout();
	}

	onManifestLoading() {
		//reset everything
		this._monitorMainControllers = false;
		this._monitorAudioControllers = false;
		this._monitorSubtitleControllers = false;
		this._controllersParsed = 0;
		this._activeControllers = 0;
		this._mainControllersReady = false;
		this._audioControllersReady = false;
		this._subtitleControllersReady = false;
		this._allReady = false;
		this._audioControllersActive = false;

		this._mainLiveTime = undefined;
		this._audioLiveTime = undefined;
		this._mainLevelStartTime = undefined;
		this._mainLevelEndTime = undefined;
		this._audioLevelStartTime = undefined;
		this._audioLevelEndTime = undefined;
		this._combinedLiveTime = undefined;
		this._mainBasePdt = undefined;
		this._audioBasePdt = undefined;
		this._basePdt = undefined;
		this._startPdt = undefined;

		this._mainLive = true;
		this._audioLive = true;
		this._startTime = -1;
		this._liveStreamsStartAtLiveTime = true;

		this.disarmMainStreamTimeout();
		this.disarmAudioStreamTimeout();
		this._mainTimedOut = false;
		this._audioTimedOut = false;
		this._defaultTimeout = 30000;
		this._vidReadyForDispose = false;
		this._audReadyForDispose = false;

		this._targetDuration = undefined;
	}

	onManifestParsed(data) {
		if(data.levels.length > 0) {
			this._monitorMainControllers = true;
		}

		this.checkParsingFinished();
	}

	onAudioTracksUpdated(data) {
		if(data.audioTracks.length > 0) {
			this._monitorAudioControllers = true;
		}

		this.checkParsingFinished();
	}

	onSubtitleTracksUpdated(data) {
		if(data.subtitleTracks.length > 0) {
			this._monitorSubtitleControllers = true;
		}

		this.checkParsingFinished();
	}

	onLevelUpdated(data) {
		this._mainLive = data.details.live;
		this._mainLiveTime = this.determineLiveTime(data.details, data.sliding, 'main');
		this._mainLevelStartTime = data.details.fragments[0].start;
		this._mainLevelEndTime = data.details.totalduration;
		this.determineCombinedLiveTime();

		if(this._monitorMainControllers && !this._mainControllersReady && !this._allReady) {
			this._targetDuration = data.details.averagetargetduration;
			this._defaultTimeout = data.details.averagetargetduration * 3000;
			this._mainBasePdt = this.determineBasePdt(data.details, 'main');
			this._mainControllersReady = true;

			this.checkReady();
		}
	}

	onAudioTrackUpdated(data) {
		this._audioLive = data.details.live;
		this._audioLiveTime = this.determineLiveTime(data.details, data.sliding, 'audio');
		this._audioLevelStartTime = data.details.fragments[0].start;
		this._audioLevelEndTime = data.details.totalduration;
		this.determineCombinedLiveTime();

		if(this._monitorAudioControllers && !this._audioControllersReady && !this._allReady) {
			this._audioBasePdt = this.determineBasePdt(data.details, 'audio');
			this._audioControllersReady = true;

			this.checkReady();
		}
	}

	onSubtitleTrackUpdated(/*data*/) {
		if(this._monitorSubtitleControllers && !this._subtitleControllersReady && !this._allReady) {
			this._subtitleControllersReady = true;

			this.checkReady();
		}
	}

	onAudioTrackSwitching() {
		let trackId = this.hls.audioTrack;
		let rendition = this.hls.audioTrackController.getRenditionNumber(trackId);
		let altAudio = !!this.hls.audioTracks[trackId].renditions[rendition].url;

		if(this._audioControllersActive && this.hls.audioStreamController.pausedTimer) {
			this.hls.audioStreamController.unpauseController();
		}

		if(altAudio) {
			this._audioControllersActive = true;
			this.armAudioStreamTimeout();
		}
		else {
			this._audioControllersActive = false;
			this.disarmAudioStreamTimeout();
		}
	}

	onStreamDesync(data) {
		let desync = data.desync / 1000;
		if(desync < 0) {
			//negative desync - main PDT is earlier than audio PDT
			this._mainLevelStartTime += desync;
			this._mainLevelEndTime += desync;
			this._mainLiveTime += desync;
		}
		else if(desync > 0) {
			//positive desync - audio PDT is earlier than main PDT
			this._audioLevelStartTime -= desync;
			this._audioLevelEndTime -= desync;
			this._audioLiveTime -= desync;
		}
	}

	onFragBuffered(data) {
		if(data.frag.type === 'main') {
			this.disarmMainStreamTimeout();
			this.armMainStreamTimeout();
		}
		else if(data.frag.type === 'audio') {
			this.disarmAudioStreamTimeout();
			this.armAudioStreamTimeout();
		}

		if(this._audioControllersActive) {
			let audioStreamController = this.hls.audioStreamController;
			let mainStreamController = this.hls.streamController;
			let audioGapTolerance = 0.2;
			let desiredPlayPoint = mainStreamController.bufferBeforeSeeking;

			if(desiredPlayPoint === undefined && this.media) {
				desiredPlayPoint = this.media.currentTime;
			}

			//if we have a known audio gap, make the gap tolerance in the buffer check longer
			//this prevents the audio stream controller getting stuck on unpaused after loading past an audio gap
			if(audioStreamController.tracks[audioStreamController.trackId].containsGaps) {
				audioGapTolerance = this._targetDuration * 1.1; //make it slightly longer than one target duration
			}
			//The 0.2 allows small gaps to be ingnored. Otherwise the audio buffer is left paused when it's behind. Possibly could be bigger.
			let mainBufferEnd = BufferHelper.bufferInfo(mainStreamController.videoBuffer, desiredPlayPoint, 0.2).end;
			let audioBufferEnd = BufferHelper.bufferInfo(audioStreamController.mediaBuffer, desiredPlayPoint, audioGapTolerance).end;

			if(audioStreamController.pausedTimer && ((audioBufferEnd - mainBufferEnd) < (this._targetDuration / 2))) {
				audioStreamController.unpauseController();
				logger.log('onFragBuffered audio processing resumed');
			}
			else if(
				!(audioStreamController.pausedTimer)
				&& ((audioBufferEnd - mainBufferEnd) > (this._targetDuration / 2))
				&& BufferHelper.isBuffered(audioStreamController.mediaBuffer, this.media.currentTime + (this._targetDuration * 2))
			) {
				audioStreamController.pauseController();
				logger.log('onFragBuffered audio processing paused');
			}
		}
	}

	onError(data) {
		if(data.fatal) {
			//hlsJs is about to be halted, don't need the timeouts any more
			this.disarmMainStreamTimeout();
			this.disarmAudioStreamTimeout();
		}
	}

	armMainStreamTimeout() {
		if(this._mainStreamTimeoutTimer === null) {
			this._mainStreamTimeoutTimer = setTimeout(() => {
				this._mainTimedOut = true;
				logger.log('main stream timeout timer expired');
			}, this.getTimeoutValue());

			logger.log('main stream timeout timer armed');
		}
	}

	armAudioStreamTimeout() {
		if(this._audioStreamTimeoutTimer === null) {
			this._audioStreamTimeoutTimer = setTimeout(() => {
				if(this._audioControllersActive && this.hls.audioStreamController.pausedTimer && !this._audioForcedUnpause) {
					this._audioForcedUnpause = true;
					this.hls.audioStreamController.unpauseController();
					logger.log('audio stream timeout force unpause');

					//Timer is not rearmed. Not sure how to re-arm it right now; the below should work but not sure if it's safe...
					//this._audioStreamTimeoutTimer = null;
					//this.armAudioStreamTimeout();
				}
				else {
					this._audioTimedOut = true;
					logger.log('audio stream timeout timer expired');
				}

			}, this.getTimeoutValue());

			logger.log('audio stream timeout timer armed');
		}
	}

	disarmMainStreamTimeout() {
		if(this._mainStreamTimeoutTimer) {
			clearTimeout(this._mainStreamTimeoutTimer);
			this._mainStreamTimeoutTimer = null;
			logger.log('main stream timeout timer disarmed');
		}
		this._mainTimedOut = false;
	}

	disarmAudioStreamTimeout() {
		if(this._audioStreamTimeoutTimer) {
			clearTimeout(this._audioStreamTimeoutTimer);
			this._audioStreamTimeoutTimer = null;
			logger.log('audio stream timeout timer disarmed');
		}
		this._audioTimedOut = false;
		this._audioForcedUnpause = false;
	}

	getTimeoutValue() {
		let timeout = this._defaultTimeout;
		let configTimeout = 0;

		if (this.config) {
			configTimeout = this.config.streamTimeOut;
		}
		timeout = Math.max(timeout, configTimeout);

		return timeout;
	}

	checkParsingFinished() {
		this._controllersParsed++;

		if(this._controllersParsed === 3) {
			this._controllersParsed = 0;
			this._activeControllers = this._monitorMainControllers + this._monitorAudioControllers + this._monitorSubtitleControllers;
			logger.log(`all manifest parsed events received, number of active controllers: ${this._activeControllers}`);
			this.hls.trigger(Event.STREAM_ALL_LEVELS_PARSED, { main: this._monitorMainControllers, audio: this._monitorAudioControllers,
				subtitle: this._monitorSubtitleControllers });
		}
	}

	checkIfRenditionsExist() {
		return this._monitorAudioControllers;
	}

	checkIfRenditionsAreActive() {
		return this._audioControllersActive;
	}

	checkReady() {
		let expectedLoads = this._activeControllers;

		if(this._monitorAudioControllers && !this._audioControllersActive) {
			//If we're starting on in-mux, we won't get an AUDIO_TRACK_LOADED
			expectedLoads -= 1;
		}

		if(this._mainControllersReady + this._audioControllersReady + this._subtitleControllersReady === expectedLoads) {
			if(this._mainBasePdt && this._audioBasePdt) {
				let mainBasePdt = this._mainBasePdt.getTime();
				let audioBasePdt = this._audioBasePdt.getTime();
				let mainTd = this.hls.levels[this.hls.loadLevel].details.targetduration * 1000;
				let audioTd = this.hls.audioStreamController.tracks[this.hls.audioStreamController.trackId].details.targetduration * 1000;
				let variance = (mainTd + audioTd) / 4;

				//sometimes we can get a mismatch where the pdts are slightly different but in actual fact are really the same,
				//so we will only trigger this if we are out by factor of the average of 1/2 the target duration of both streams
				if(mainBasePdt < audioBasePdt - variance || mainBasePdt > audioBasePdt + variance) {
					let desync = mainBasePdt - audioBasePdt;
					logger.log(`base pdt desync: ${desync}`);
					//this.hls.trigger(Event.STREAM_DESYNC, { desync });
					this.hls.trigger(Event.STREAM_DESYNC, { desync, pdtToAlignTo: desync > 0 ? mainBasePdt : audioBasePdt, variance });
				}
			}
			this._basePdt = this.determineCombinedBasePdt();
			//This event signals we're ready to do PDT based calculations
			//once we emit it, bam-hls will send the start PDT to hlsJs, if it has one
			this.hls.trigger(Event.STREAM_BASEPDT_FOUND, { basePdt: this._basePdt });

			this.armMainStreamTimeout();
			if(this._audioControllersActive) {
				this.armAudioStreamTimeout();
			}

			this._allReady = true;
			let startTime = this.determineStartTime();
			logger.log(`all expected level loaded events received, start time: ${startTime}, expected loads: ${expectedLoads}`);
			this.hls.trigger(Event.STREAM_ALL_LEVELS_LOADED, { startTime });
		}
	}


	checkReadyForDispose(videoTrackReady, audioTrackReady) {
		let toDispose = this._activeControllers;
		let haveDisposed = 0;
		let readyForDispose = false;

		logger.log( 'checkReadyForDispose Count ' + toDispose);

		if(this._monitorAudioControllers && !this._audioControllersActive) {
			logger.log( 'checkReadyForDispose Not using renditions');
			toDispose -= 1;
		}

		if (videoTrackReady !== undefined) {
			logger.log( 'checkReadyForDispose Video');
			this._vidReadyForDispose = videoTrackReady;
		}
		if (audioTrackReady !== undefined) {
			logger.log( 'checkReadyForDispose Audio');
			this._audReadyForDispose = audioTrackReady;
		}

		if (this._vidReadyForDispose) {
			haveDisposed += 1;
		}
		if (this._audReadyForDispose) {
			haveDisposed += 1;
		}

		if (toDispose === haveDisposed) {
			readyForDispose = true;
		}

		logger.log( 'checkReadyForDispose ' + readyForDispose);

		return readyForDispose;
	}


	determineLiveTime(details, sliding, type) {
		let liveSyncTime;
		//seconds mode
		if(this.config.liveSyncDuration !== undefined) {
			//get the target time
			let targetTime = details.totalduration + sliding - this.config.liveSyncDuration;
			//if start of frag 0 is later than target time, set live sync time to start of frag 0
			if(targetTime < details.fragments[0].start) {
				logger.warn(`StreamCoordinator::determineLiveTime: current manifest for ${type} shorter than liveSyncDuration, setting to start of manifest`);
				liveSyncTime = details.fragments[0].start;
			}
			else {
				//look for the last frag in the manifest that starts before the target time
				for(let i = details.fragments.length - 1; i >= 0; i--) {
					if(details.fragments[i].start <= targetTime) {
						liveSyncTime = details.fragments[i].start;
						break;
					}
				}
			}
		}
		//frag count mode
		else if(this.config.liveSyncDurationCount !== undefined) {
			//check if there are enough frags in the manifest, if not, set live sync time to start of frag 0
			if(details.fragments.length < this.config.liveSyncDurationCount) {
				logger.warn(`StreamCoordinator::determineLiveTime: current manifest for ${type} has less frags than liveSyncDurationCount, setting to start of manifest`);
				liveSyncTime = details.fragments[0].start;
			}
			else {
				liveSyncTime = details.fragments[details.fragments.length - this.config.liveSyncDurationCount].start;
			}
		}
		//neither live sync config setting has been defined - this should not be possible
		else {
			throw new Error('StreamCoordinator::determineLiveTime: No live sync offset found');
		}

		logger.log(`${type} live sync time: ${liveSyncTime}`);
		return liveSyncTime;
	}

	determineCombinedLiveTime() {
		let liveTime;
		//if the audio controllers are currently inactive, the live time is the main live time
		if(!this._monitorAudioControllers || !this._audioControllersActive) {
			liveTime = this._mainLiveTime;
		}
		//combined live time is the earliest of the two live times
		//if this time is before one of the manifest starts, that manifest start becomes the live time
		else if (this._mainLiveTime && this._audioLiveTime) {
			liveTime = Math.min(this._mainLiveTime, this._audioLiveTime);
			liveTime = Math.max(liveTime, this._mainLevelStartTime, this._audioLevelStartTime);
		}
		//live time should only ever increase, if the new live time is greater than the old one, emit an event and update
		if(liveTime && (liveTime > this._combinedLiveTime || this._combinedLiveTime === undefined)) {
			let lastLiveTime = this._combinedLiveTime;
			this._combinedLiveTime = liveTime;
			logger.log(`combined live sync time: ${liveTime}`);
			this.hls.trigger(Event.STREAM_LIVETIME_CHANGED, { liveTime, lastLiveTime });
		}
	}

	determineStartTime() {
		//LSSALT takes priority if it is true and stream is live
		//if we've got both main and audio active, check both are live, as if one isn't that likely means the other one is going to stop being live next load
		let liveStream = this._mainLive;

		if(liveStream && this._monitorAudioControllers && this._audioControllersActive) {
			liveStream = this._mainLive && this._audioLive;
		}

		if(liveStream && this._liveStreamsStartAtLiveTime) {
			logger.log('live stream with LSSALT enabled, setting live time as start time');
			return this._combinedLiveTime;
		}
		//if we have a start PDT, calculate the position for that PDT and make that the new start time
		//if it lands in a disco/gap, go ahead to the start of the next frag
		let startTime;

		if(this._startPdt) {
			startTime = this.getTimeForPdt(this._startPdt);
		}

		if(!startTime) {
			startTime = this._startTime;
		}
		//if we're at this point, we're not live
		//use startTime, after adjusting to make sure startTime is not outside allowable range
		if(!this._monitorAudioControllers || !this._audioControllersActive) {
			startTime = Math.max(startTime, this._mainLevelStartTime);
			startTime = Math.min(startTime, this._mainLevelEndTime);
		}
		else {
			startTime = Math.max(startTime, this._mainLevelStartTime, this._audioLevelStartTime);
			startTime = Math.min(startTime, this._mainLevelEndTime, this._audioLevelEndTime);
		}

		if(liveStream) {
			startTime = Math.min(startTime, this._combinedLiveTime);
		}

		return startTime;
	}

	determineBasePdt(details, type) {
		const fragments = details.fragments;
		const fragCount = fragments.length;
		for(let i = 0; i < fragCount; i++) {
			let frag = fragments[i];
			const rawPdt = frag.rawProgramDateTime;
			if(rawPdt) {
				let date = new Date(new Date(rawPdt).valueOf() - (frag.start * 1000));
				logger.log(`${type} base PDT: ${date}`);
				return date;
			}
		}

		logger.log(`no base PDT for ${type}`);
		return null;
	}

	determineCombinedBasePdt() {
		let basePdt;
		// Needed only to force unified pdt ASAP in recovery cases
		// If timing is changed this may not be needed in the future
		if(this._resumeBasePdt) {
			basePdt = this._resumeBasePdt;
		}
		else if(this._mainBasePdt && this._audioBasePdt) {
			if(this._mainBasePdt.getTime() >= this._audioBasePdt.getTime()) {
				basePdt = this._mainBasePdt;
			}
			else {
				basePdt = this._audioBasePdt;
			}
		}
		else if(this._mainBasePdt) {
			basePdt = this._mainBasePdt;
		}
		else {
			basePdt = null;
		}

		return basePdt;
	}

	_findFragList() {
		let fragList;

		const hls = this.hls;

		const levels = hls.levels;
		let details = levels[hls.loadLevel].details;
		if( details) {
			fragList = details.fragments;
		}
		else {
			const levelsCount = levels.length;
			for(let i = 0; i < levelsCount; i++) {
				details = levels[i].details;
				if(details) {
					fragList = details.fragments;
					break;
				}
			}
		}

		return fragList;
	}

	getTimeForPdt(pdt) {
		let baseTime = pdt.getTime();
		let fragList = this._findFragList();

		//look for a frag that has a pdt range that contains the frag we're looking for
		const fragCount = fragList.length;
		for(let i = 0; i < fragCount; i++) {
			let frag = fragList[i];
			let fragPdt = frag.pdt;
			let fragEndPdt = fragPdt + (frag.duration * 1000);

			if(baseTime >= fragPdt && baseTime <= fragEndPdt) {
				//calculate difference between frag PDT and start PDT, then add that to frag start to get our start time
				return Number((frag.start + (baseTime - fragPdt) / 1000).toFixed(6));
			}
			else if(baseTime < fragEndPdt) {
				//if we're here, then the previous frag ends before the start PDT but this frag starts after the start PDT
				//when we have a gap, use the start of the frag after the start PDT
				return Number((frag.start).toFixed(6));
			}
			// this could be done as a post-check of the loop...
			else if(i === fragCount - 1) {
				return Number((frag.start + (baseTime - fragPdt) / 1000).toFixed(6));
			}
		}

		return null;
	}

	getPdtForTime(time) {

		let fragList = this._findFragList();

		if(time < fragList[0].start) {
			//before first frag
			let offset = fragList[0].start - time;
			return new Date(fragList[0].pdt - (offset * 1000));
		}

		let gapTotal = 0;
		let nextGapTotal = 0;

		const fragCount = fragList.length;
		for(let i = 0; i < fragCount; i++) {
			if (i === fragCount - 1) {
				//last frag, just calculate from this start time
				let offset = time - (fragList[i].start - (gapTotal / 1000));
				return new Date(fragList[i].pdt + (offset * 1000));
			}
			else {
				let nextFrag = fragList[i + 1];
				if (nextFrag.gap > 0 && !this._audioControllersActive) {
					nextGapTotal += nextFrag.gap;
				}

				let thisFrag = fragList[i];
				if (thisFrag.start - (gapTotal / 1000) < time && nextFrag.start - (nextGapTotal / 1000) > time) {
					//time falls within this frag, use this one
					let offset = time - (thisFrag.start - (gapTotal / 1000));
					return new Date(thisFrag.pdt + (offset * 1000));
				}
				gapTotal = nextGapTotal;
			}
		}

		return null;
	}

	set startTime(value) {
		this._startTime = value;
	}

	set liveStreamsStartAtLiveTime(value) {
		this._liveStreamsStartAtLiveTime = !!value;
	}

	set startPdt(value) {
		this._startPdt = value;
	}

	get basePdt() {
		let basePdt = this._basePdt;

		if(this._resumeBasePdt) {
			basePdt = this._resumeBasePdt; //Need this ASAP to avoid timeline confusion
		}
		return basePdt;
	}

	get resumeBasePdt() {
		return this._resumeBasePdt;
	}

	set resumeBasePdt(value) {

		logger.log(`StreamCoordinator: resumeBasePdt set to ${value}`);

		this._resumeBasePdt = value;
	}

}

export default StreamCoordinator;



// WEBPACK FOOTER //
// ./src/hlsjs/controller/stream-coordinator.js